/**
 * The two pieces both desktop TO-DO panels are made of.
 *
 * GOTY had them first (lane D3); MY INTERESTS grew the same panel on
 * 2026-08-24 (DESIGN-CHANGELOG, "the `?` NO CALL YET tier is now a TO DO
 * block") and the design says so in as many words — "same geometry as the GOTY
 * panel". Two copies of this markup would be two places for 2/3 to become 3/4,
 * so the markup lives here and each board keeps only what differs: what the
 * buttons under the cover do, and what the meta line says.
 *
 * What is NOT shared is the panel shell — its width, header and empty states
 * sit in the screens, because each board's copy and drop contract is its own.
 */

import { CoverArt } from '../../components/CoverArt.tsx';
import { useArt } from '../../components/useArt.ts';
import type { CapsuleVM } from '../../store/selectors.ts';
import { useHoverTilt } from '../useHoverTilt.ts';
import type { BoardDnd } from '../useHtml5BoardDnd.ts';

const MONO = 'var(--gt-font-mono)';

/**
 * The featured TO-DO cover. Not a `BoardCapsule`: it has no caption row, its
 * sheen sits under two absolutely-positioned children, and its tilt id has to
 * be prefixed because the same game may also be mounted as a board capsule
 * after a placement or a call lands (§3.1).
 *
 * `tiltNs` is that prefix. The two boards never mount together, but a shared
 * component that hard-codes one namespace is a trap waiting for the day
 * something does.
 */
export function TodoFeaturedCover({
  feat,
  dnd,
  tiltNs,
}: {
  feat: CapsuleVM;
  dnd: BoardDnd;
  tiltNs: string;
}): JSX.Element {
  const art = useArt(feat.id + 'p', feat.coverCands);
  const tilt = useHoverTilt(tiltNs + ':' + String(feat.id));
  const drag = dnd.dragProps(feat.id, () => ({
    id: feat.id,
    nameUp: feat.nameUp,
    coverBg: art.hasCover ? art.cover : '',
    fall: feat.fall,
  }));

  return (
    <div
      {...drag}
      ref={tilt.ref}
      {...tilt.handlers}
      style={{
        position: 'relative',
        aspectRatio: '2/3',
        border: '1px solid var(--gt-border-2)',
        background: feat.fall,
        overflow: 'hidden',
        cursor: 'grab',
        opacity: dnd.dragId === feat.id ? '.18' : '1',
        transition: 'opacity .12s ease',
        ...tilt.rest,
      }}
    >
      <CoverArt style={{ position: 'absolute', inset: 0 }} fs={15} ini={feat.ini} hasCover={art.hasCover} cover={art.cover} />
      <div ref={tilt.sheenRef} style={tilt.restSheen} />
      <div
        style={{
          position: 'absolute',
          top: 6,
          right: 6,
          color: 'rgba(255,255,255,.55)',
          fontSize: 13,
          letterSpacing: '2px',
          textShadow: '1px 1px 0 rgba(0,0,0,.8)',
          zIndex: 2,
        }}
      >
        ⠿
      </div>
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(transparent,rgba(5,5,8,.85))',
          padding: '18px 8px 7px',
          fontFamily: MONO,
          fontSize: '8.5px',
          letterSpacing: '.06em',
          color: 'rgba(255,255,255,.75)',
          textAlign: 'center',
          zIndex: 2,
        }}
      >
        ⇱ DRAG ONTO THE BOARD
      </div>
    </div>
  );
}
